import {
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Box,
  Flex,
  Badge,
  Button,
  Spinner,
  Stack,
  Card,
  CardHeader,
  CardBody,
  Heading,
  HStack,
  Icon,
  Image,
  Text,
  Checkbox,
  useColorModeValue
} from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { MdCheck, MdClose, MdCleaningServices } from 'react-icons/md';
import { FiEye } from 'react-icons/fi';
import { FcClock } from 'react-icons/fc';
import { RiAuctionLine } from 'react-icons/ri';
import { MdInventory2 } from 'react-icons/md';
import { ScrapejobModel } from 'models/scrapejob/ScrapejobModel';
import { ItemGrid, ListingGrid } from 'components';
import { NavigationService } from 'services';
import { ProcessStatus } from 'models/types';
import { ItemModel } from 'models';

export interface DetailedScrapeJobCardProps {
  scrapejob: ScrapejobModel;
  onApprove?: (scrapejobId: string) => void;
  onReject?: (scrapejobId: string) => void;
  onSanitize?: (scrapejobId: string) => void;
  approveLoading?: boolean;
  rejectLoading?: boolean;
  sanitizeLoading?: boolean;
  defaultAccordionOpen?: boolean;
  selectionMode?: boolean;
  selected?: boolean;
  onSelect?: () => void;
}

export default function DetailedScrapeJobCard({
  scrapejob,
  onApprove,
  onReject,
  onSanitize,
  approveLoading = false,
  rejectLoading = false,
  sanitizeLoading = false,
  defaultAccordionOpen = false,
  selectionMode = false,
  selected = false,
  onSelect
}: DetailedScrapeJobCardProps) {
  const navigate = useNavigate();
  const selectedBgColor = useColorModeValue('blue.50', 'blue.900');
  const selectedBorderColor = useColorModeValue('blue.400', 'blue.500');
  const hoverBgColor = useColorModeValue('gray.50', 'gray.700');

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'pending':
        return 'yellow';
      case 'approved':
        return 'green';
      case 'rejected':
        return 'red';
      default:
        return 'gray';
    }
  };

  const isDemoted = scrapejob.status === ProcessStatus.PENDING;

  const handleApprove = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onApprove && !approveLoading && !rejectLoading) {
      onApprove(scrapejob.id);
    }
  };

  const handleReject = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onReject && !approveLoading && !rejectLoading) {
      onReject(scrapejob.id);
    }
  };

  const handleSanitize = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onSanitize && !sanitizeLoading) {
      onSanitize(scrapejob.id);
    }
  };

  const handleViewScrapejob = (e: React.MouseEvent) => {
    e.stopPropagation();
    navigate(NavigationService.getScrapejobDetailsPath(scrapejob.id));
  };

  const handleItemClick = (item: ItemModel) => {
    navigate(NavigationService.getItemDetailsPath(item.id, item.getDisplayName(), isDemoted));
  };

  const handleListingNameClick = (id: string, name: string) => {
    navigate(NavigationService.getListingDetailsPath(id, name, isDemoted));
  };

  const handleListingArtistClick = (id: string, name: string) => {
    navigate(NavigationService.getArtistDetailsPath(id, name));
  };

  const handleCardClick = () => {
    if (selectionMode && onSelect) {
      onSelect();
    }
  };

  return (
    <Card
      variant="outline"
      borderRadius="lg"
      cursor={selectionMode ? 'pointer' : 'default'}
      onClick={handleCardClick}
      borderColor={selected ? selectedBorderColor : undefined}
      borderWidth={selected ? '2px' : '1px'}
      bg={selected ? selectedBgColor : undefined}
      _hover={selectionMode ? { bg: selected ? selectedBgColor : hoverBgColor } : undefined}
      transition="all 0.2s"
    >
      <CardHeader borderBottomWidth="1px">
        <Flex direction={{ base: 'column', md: 'row' }} align={{ base: 'flex-start', md: 'center' }} justify="space-between" gap={4}>
          <Stack spacing={2} alignItems="flex-start">
            <HStack spacing={3}>
              {selectionMode && (
                <Checkbox
                  isChecked={selected}
                  onChange={(e) => {
                    e.stopPropagation();
                    onSelect?.();
                  }}
                  size="lg"
                  colorScheme="blue"
                />
              )}
              {scrapejob.siteImage && (
                <Image
                  src={scrapejob.siteImage}
                  alt={scrapejob.getSiteName()}
                  boxSize="40px"
                  objectFit="contain"
                  borderRadius="md"
                  bg="white"
                  p={1}
                />
              )}
              <Heading fontSize="md">{scrapejob.getSiteName()}</Heading>
            </HStack>
            <HStack spacing={3}>
              <Badge colorScheme={getStatusColor(scrapejob.status)}>{scrapejob.status}</Badge>
              <Flex direction="row" alignItems="center" gap={1}>
                <Icon as={FcClock} w={3} h={3} />
                <Text fontSize="xs">{scrapejob.getStartTimeFormatted()}</Text>
              </Flex>
            </HStack>
          </Stack>
          <HStack spacing={2}>
            {!selectionMode && (
              <Button
                size="sm"
                variant="outline"
                leftIcon={<FiEye />}
                onClick={handleViewScrapejob}
              >
                View
              </Button>
            )}
            {!selectionMode && onSanitize && (
              <Button
                size="sm"
                colorScheme="teal"
                leftIcon={sanitizeLoading ? <Spinner size="xs" /> : <MdCleaningServices />}
                isLoading={sanitizeLoading}
                isDisabled={approveLoading || rejectLoading}
                onClick={handleSanitize}
              >
                Sanitize
              </Button>
            )}
            {!selectionMode && scrapejob.status.toLowerCase() === 'pending' && (
              <>
                {onApprove && (
                  <Button
                    colorScheme="green"
                    size="sm"
                    leftIcon={approveLoading ? <Spinner size="xs" /> : <MdCheck />}
                    isLoading={approveLoading}
                    isDisabled={rejectLoading}
                    onClick={handleApprove}
                  >
                    Approve
                  </Button>
                )}
                {onReject && (
                  <Button
                    colorScheme="red"
                    size="sm"
                    leftIcon={rejectLoading ? <Spinner size="xs" /> : <MdClose />}
                    isLoading={rejectLoading}
                    isDisabled={approveLoading}
                    onClick={handleReject}
                  >
                    Reject
                  </Button>
                )}
              </>
            )}
          </HStack>
        </Flex>
      </CardHeader>
      <CardBody>
        <Stack gap={4}>
          {/* Items Accordion */}
          {scrapejob.items.length > 0 && (
            <Box>
              <Accordion allowToggle defaultIndex={defaultAccordionOpen ? [0] : []}>
                <AccordionItem border="1px" borderColor="gray.200" borderRadius="md">
                  <h2>
                    <AccordionButton>
                      <Flex flex="1" textAlign="left" flexDirection="row" gap={1} alignItems="center" py={2}>
                        <Icon as={MdInventory2} w={3} h={3} />
                        <Heading fontSize="sm">
                          Items ({scrapejob.items.length})
                        </Heading>
                      </Flex>
                      <AccordionIcon />
                    </AccordionButton>
                  </h2>
                  <AccordionPanel pb={4}>
                    <ItemGrid
                      items={scrapejob.items}
                      isLoading={false}
                      showFilters={false}
                      gridDisplay={true}
                      showMetadata={true}
                      onItemNameClick={handleItemClick}
                      paginate={false}
                    />
                  </AccordionPanel>
                </AccordionItem>
              </Accordion>
            </Box>
          )}

          {/* Listings Accordion */}
          {scrapejob.listings.length > 0 && (
            <Box>
              <Accordion allowToggle defaultIndex={defaultAccordionOpen ? [0] : []}>
                <AccordionItem border="1px" borderColor="gray.200" borderRadius="md">
                  <h2>
                    <AccordionButton>
                      <Flex flex="1" textAlign="left" flexDirection="row" gap={1} alignItems="center" py={2}>
                        <Icon as={RiAuctionLine} w={3} h={3} />
                        <Heading fontSize="sm">
                          Listings ({scrapejob.listings.length})
                        </Heading>
                      </Flex>
                      <AccordionIcon />
                    </AccordionButton>
                  </h2>
                  <AccordionPanel pb={4}>
                    <ListingGrid
                      gridListings={scrapejob.listings}
                      isLoading={false}
                      showFilters={false}
                      onListingNameClick={handleListingNameClick}
                      onListingArtistClick={handleListingArtistClick}
                    />
                  </AccordionPanel>
                </AccordionItem>
              </Accordion>
            </Box>
          )}
        </Stack>
      </CardBody>
    </Card>
  );
}
